/**
 * swUpdate — keep long-lived tabs and the installed PWA on the current build.
 *
 * The service worker registers with `autoUpdate`, but the browser only checks for
 * a new sw.js on NAVIGATION. A tab or installed-PWA window that stays open never
 * navigates, so it can keep serving last week's precached bundle indefinitely —
 * users retest a "fixed" bug and hit the old code (bulk-export incident,
 * 2026-07-16). Two counter-measures:
 *
 *  1. Proactively re-check for a new service worker: on load, on a 5-minute
 *     interval, and on every signal that the user has come back to the window —
 *     visibility, FOCUS (app-switching, which leaves visibility untouched) and
 *     regaining network. Throttled so a burst of alt-tabs is one check.
 *  2. When a new service worker takes control (autoUpdate skips waiting, so this
 *     fires as soon as one installs), reload ONCE to pick up the new build —
 *     after that point the old chunk names 404 anyway, so a prompt, warned reload
 *     beats a random crash-reload mid-click (see chunkReload.ts).
 *
 *     Never by force over someone's work (meetcs2, 17 Oct 2026). A CS adding
 *     six items to a board meeting was thrown back to step one every time the
 *     site deployed, which on a busy day is several times an hour. While any
 *     form says it holds unsaved work (`unsavedWork.ts`) the reload waits: a
 *     toast says "A new version of Karr is ready" with a Reload button, and the
 *     reload happens when the person presses it, moves to another page with
 *     nothing left open (`applyWaitingUpdate`, from the router), or leaves the
 *     tab with the form closed. A chunk that 404s in the meantime is still
 *     healed by chunkReload.ts, which is why the long forms also keep a draft.
 *
 * Dependency-free apart from the toast, and side-effect-guarded, so it can be
 * dropped into Karr Travel / Karr Lab like chunkReload.ts.
 */
import { toast } from '@/shared/ui/toast';
import { isRunningStaleBuild } from './buildFreshness';
import { reloadForFreshChunks } from './chunkReload';
import { hasUnsavedWork } from './unsavedWork';

// 5 minutes, not 15. On 2026-07-29 the founder retested a fix roughly ten minutes
// after it deployed, saw the old build, and reasonably concluded nothing had been
// shipped. The watcher was working exactly as designed — the design was just
// slower than a person checking their own bug report.
const CHECK_INTERVAL_MS = 5 * 60_000;
// A check is a conditional GET of sw.js (a 304 in the common case), but there is
// no reason to fire several in a row when someone alt-tabs quickly.
const CHECK_THROTTLE_MS = 30_000;
const RELOAD_DELAY_MS = 3_500;

export function installSwUpdateWatcher(): void {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

    // A controller existing at startup means the SW already governs this page;
    // the first controllerchange on a FRESH install must not reload.
    let hadController = !!navigator.serviceWorker.controller;
    let reloading = false;

    navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!hadController) { hadController = true; return; }
        if (reloading) return;
        reloading = true;
        newVersion(() => window.location.reload());
    });

    let lastCheck = 0;
    const check = () => {
        const now = Date.now();
        if (now - lastCheck < CHECK_THROTTLE_MS) return;
        lastCheck = now;
        navigator.serviceWorker.getRegistration()
            .then((reg) => reg?.update())
            .catch(() => { /* offline or unregistered — never break the app over an update check */ });

        // Second line, independent of the worker. A worker that never updates
        // never fires controllerchange, and the stale-chunk heal cannot see
        // this case either: the old chunks are still on the CDN, so nothing
        // fails and nothing is detected. Comparing the entry chunk this tab is
        // running against the one the network would serve notices the deploy
        // whatever the worker believes.
        void isRunningStaleBuild().then((stale) => {
            if (!stale || reloading) return;
            reloading = true;
            newVersion(() => reloadForFreshChunks({ force: true }));
        }).catch(() => { /* never break the app over a freshness check */ });
    };

    window.setInterval(check, CHECK_INTERVAL_MS);

    // Three different "the user came back" signals, because none of them covers
    // the case on its own:
    //   • visibilitychange — tab backgrounded, or the window minimised.
    //   • focus — switching to ANOTHER APPLICATION and back. This is the one that
    //     was missing: alt-tabbing to a terminal leaves visibilityState as
    //     'visible', so the founder's whole retest loop fired nothing at all.
    //   • online — a laptop that was asleep or off the network reconnects.
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check();
        // A held update goes the moment nobody is looking and nothing is open.
        else applyWaitingUpdate();
    });
    window.addEventListener('focus', check);
    window.addEventListener('online', check);

    // Register the worker itself. This lives here rather than in the
    // registerSW.js vite-plugin-pwa would generate, because that file never
    // handles the rejection (`injectRegister: null` in vite.config.ts turns it
    // off). Listeners above are attached first, so a fresh install's
    // controllerchange is seen and correctly ignored.
    registerServiceWorker();

    // And one immediately, so a tab that has been open since before a deploy
    // learns about it on the next full page load rather than up to an interval later.
    check();
}

/** The reload a new version is waiting on, while a form holds it back. */
let waiting: (() => void) | null = null;
const READY_TOAST_ID = 'karr-new-version';

/**
 * A new build is here. Nobody looking and nothing open: swap silently. Someone
 * looking at a page with nothing open: say so, and reload in a moment. Someone
 * halfway through a form: say so, and leave the reload to them.
 */
function newVersion(reload: () => void): void {
    if (hasUnsavedWork()) { hold(reload); return; }
    if (document.visibilityState !== 'visible') { reload(); return; }
    toast.info('Karr was updated', {
        description: 'Loading the new version…',
        duration: RELOAD_DELAY_MS,
        action: { label: 'Reload now', onClick: reload },
    });
    // They may have started typing in the 3.5 seconds the toast was up.
    window.setTimeout(() => { if (hasUnsavedWork()) hold(reload); else reload(); }, RELOAD_DELAY_MS);
}

function hold(reload: () => void): void {
    waiting = reload;
    toast.info('A new version of Karr is ready', {
        id: READY_TOAST_ID,
        description: 'Nothing you typed is lost. Finish what you are doing, then press Reload. Karr also loads it when you next move to another page.',
        duration: Infinity,
        action: { label: 'Reload', onClick: () => { waiting = null; reload(); } },
    });
}

/**
 * Load the new version that was held back, if one is and no form still holds
 * work. The router calls this on every finished navigation (`main.tsx`), and
 * the tab calls it when it is hidden.
 */
export function applyWaitingUpdate(): void {
    if (!waiting || hasUnsavedWork()) return;
    const reload = waiting;
    waiting = null;
    reload();
}

/** For tests: forget a held update. */
export function resetWaitingUpdate(): void {
    waiting = null;
}

/**
 * Install the service worker, and treat a refusal as normal.
 *
 * A page with no service worker loses offline precaching and nothing else, so a
 * failure here is not a bug and must not be reported as one. Plenty of real
 * browsers refuse: extensions and in-app webviews that wrap
 * `navigator.serviceWorker`, private/strict modes, and managed-device policies.
 * Before this was handled, each refusal surfaced as an `unhandledrejection` and
 * the crash digest filled with `Rejected ... at ServiceWorkerContainer.register`
 * from four different routes, hiding the crashes it exists to show.
 *
 * `console.debug`, deliberately: visible when you go looking, silent otherwise,
 * and never routed to the error reporter.
 */
function registerServiceWorker(): void {
    // Dev has no generated sw.js, so registering there is a guaranteed 404.
    if (!import.meta.env.PROD) return;

    const register = () => {
        // `updateViaCache: 'none'` is the whole reason a deploy is noticed at
        // all on karr.pro. Without it the browser answers `registration.update()`
        // from its own HTTP cache, and the zone's Browser Cache TTL pins /sw.js
        // for FOUR HOURS: every check below re-reads the same bytes, finds no
        // change, and the tab keeps serving the old build. `public/_headers`
        // asks for max-age=0 and is overridden at the zone, which only the
        // Cloudflare dashboard can undo — this flag does not need it.
        navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' }).catch((err: unknown) => {
            console.debug('Service worker was not registered; offline caching is off.', err);
        });
    };

    // Wait for load, as the generated registration did: the worker's precache
    // fetches should not compete with the page's own first paint.
    if (document.readyState === 'complete') register();
    else window.addEventListener('load', register, { once: true });
}
